/**
 * Pure pre-conversion gate for the event form (FBIMP-13, spec 10.3).
 *
 * Returns a map of form field name -> i18n key, empty when everything passes.
 * It returns KEYS, not translated strings: that is what keeps the function pure
 * and its test free of i18next. `EventFormMain` passes each value through `t`
 * when it sets the field error.
 *
 * Every rule is independent, so a single submit reports every problem at once
 * rather than one problem per attempt.
 */

export interface ValidateEventTimingInput {
  title?: string | null
  title_pt?: string | null
  startTime: Date | null
  finishTime: Date | null
}

const isBlank = (value?: string | null): boolean =>
  value === null || value === undefined || value.trim() === ''

/**
 * A `Date` is usable only if it is a Date at all AND its time value is a real
 * number. `new Date('2026-13-45')` constructs happily and fails only on use —
 * a naive null check lets it straight through into dayjs and `toISOString()`.
 */
const isUsableDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime())

export const validateEventTiming = (
  input: ValidateEventTimingInput
): Record<string, string> => {
  const errors: Record<string, string> = {}
  const { title, title_pt, startTime, finishTime } = input

  if (isBlank(title) && isBlank(title_pt)) {
    errors.title = 'errors.title_required'
  }

  const startUsable = isUsableDate(startTime)
  const finishUsable = isUsableDate(finishTime)

  if (!startUsable) {
    errors.startTime = 'errors.start_time_required'
  }

  if (!finishUsable) {
    errors.finishTime = 'errors.finish_time_required'
  } else if (startUsable && finishTime.getTime() <= startTime.getTime()) {
    // Equal counts as invalid: a zero-length event is a data-entry mistake,
    // not an event. This replaces rather than adds to the missing-field
    // message, so a field never carries two keys.
    errors.finishTime = 'errors.finish_time_before_start'
  }

  return errors
}
